import { useEffect } from 'react';
import MapView from '@arcgis/core/views/MapView';
import { syncExternalLayers } from 'components/MapPage/CorporateViewTabs/LayersTab/arcgis-integration/syncExternalLayers';
import type { ExternalLayer } from 'hooks/externalLayer/types';
import type { ArcGisAuthOptions } from '../arcgis-integration/externalLayerLoaderWorker';

/**
 * Syncs external ArcGIS layers on the map with feature data from ArcGIS hosted
 * layers and manages their lifecycle.
 */
export function useExternalLayerSync({
  mapView,
  externalLayers,
  externalLayerVisibilityById,
  addedExternalLayersRef,
  setIsExternalLayersReady,
  setExternalLayerLoadErrorById,
  options,
}: {
  mapView: MapView | null;
  externalLayers: ExternalLayer[];
  externalLayerVisibilityById: Map<string, boolean>;
  addedExternalLayersRef: React.MutableRefObject<Map<string, __esri.Layer[]>>;
  setIsExternalLayersReady?: React.Dispatch<React.SetStateAction<boolean>>;
  setExternalLayerLoadErrorById?: React.Dispatch<
    React.SetStateAction<Map<string, boolean>>
  >;
  options?: ArcGisAuthOptions;
}) {
  // Cleanup: remove external layers when mapView changes or unmounts
  useEffect(() => {
    if (!mapView?.map) return;

    const map = mapView.map;
    const addedLayersById = addedExternalLayersRef.current;

    return () => {
      // Clean up errors for any layer that was currently managed/added
      const managedIds = Array.from(addedLayersById.keys());
      if (managedIds.length > 0) {
        setExternalLayerLoadErrorById?.((prev) => {
          const next = new Map(prev);
          let hasChanges = false;
          for (const layerId of managedIds) {
            hasChanges = next.delete(layerId) || hasChanges;
          }
          return hasChanges ? next : prev;
        });
      }

      // Remove actual layers from the map
      for (const layers of addedLayersById.values()) {
        for (const layer of layers) {
          map.remove(layer);
        }
      }
      addedLayersById.clear();
      setIsExternalLayersReady?.(false);
    };
  }, [
    mapView,
    addedExternalLayersRef,
    setExternalLayerLoadErrorById,
    setIsExternalLayersReady,
  ]);

  // Sync external layers to map
  useEffect(() => {
    if (!mapView?.map) {
      setIsExternalLayersReady?.(false);
      return;
    }
    if (!options) {
      return;
    }

    const map = mapView.map;
    let isCancelled = false;

    const sync = async () => {
      setIsExternalLayersReady?.(false);
      const desiredExternalLayerIds = new Set(
        externalLayers.map((l) => String(l.id))
      );

      // Remove layers (and their errors) that no longer exist in the incoming externalLayers
      const addedLayersById = addedExternalLayersRef.current;
      const removedLayerIds: string[] = [];

      for (const [externalLayerId, layers] of addedLayersById) {
        if (!desiredExternalLayerIds.has(externalLayerId)) {
          for (const layer of layers) {
            map.remove(layer);
          }
          addedLayersById.delete(externalLayerId);
          removedLayerIds.push(externalLayerId);
        }
      }

      if (removedLayerIds.length > 0) {
        setExternalLayerLoadErrorById?.((prev) => {
          const next = new Map(prev);
          let hasChanges = false;
          for (const layerId of removedLayerIds) {
            hasChanges = next.delete(layerId) || hasChanges;
          }
          return hasChanges ? next : prev;
        });
      }

      // Add new layers
      for (const externalLayer of externalLayers) {
        const externalLayerId = externalLayer.id;
        if (addedLayersById.has(externalLayerId)) continue;

        const { layers, loadError } = await syncExternalLayers(
          externalLayer,
          options
        );
        if (isCancelled) {
          return;
        }

        setExternalLayerLoadErrorById?.((prev) => {
          const currentLoadError = prev.get(externalLayerId) ?? false;
          if (currentLoadError === loadError) {
            return prev;
          }

          const next = new Map(prev);
          next.set(externalLayerId, loadError);
          return next;
        });

        if (layers.length > 0) {
          const isVisible =
            externalLayerVisibilityById.get(externalLayerId) ?? true;
          for (const layer of layers) {
            clearScaleRange(layer);
            layer.visible = isVisible;
            map.add(layer);
          }
          addedLayersById.set(externalLayerId, layers);
        }
      }

      if (!isCancelled) {
        setIsExternalLayersReady?.(true);
      }
    };

    sync();
    return () => {
      isCancelled = true;
    };
    // externalLayerVisibilityById intentionally omitted - visibility is applied in toggle callback
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    externalLayers,
    mapView,
    setExternalLayerLoadErrorById,
    setIsExternalLayersReady,
    options,
  ]);
}

const clearScaleRange = (layer: __esri.Layer) => {
  if ('minScale' in layer) {
    (layer as __esri.Layer & { minScale: number }).minScale = 0;
  }
  if ('maxScale' in layer) {
    (layer as __esri.Layer & { maxScale: number }).maxScale = 0;
  }
};
